import type {
  FieldValues,
  Path,
  UseFormGetValues,
  UseFormWatch,
} from "react-hook-form";

type Extractor = "id" | "text" | "raw";
type Bucket = "equals" | "contains";

export type DepField<T extends FieldValues> = {
  name: Path<T>; // the field to watch
  key: string; // the API field key
  extract?: Extractor;
  bucket?: Bucket;
};

/**
 * Build a FiltersBuilder that watches multiple fields,
 * ignores the current one, and maps values to `equals`.
 */
export function makeFiltersBuilder<T extends FieldValues>(
  deps: readonly DepField<T>[],
  currentField: Path<T>,
) {
  return (ctx?: { watch: UseFormWatch<T>; getValues: UseFormGetValues<T> }) => {
    if (!ctx) return;

    const equals: Record<string, string | number | boolean | null> = {};
    const contains: Record<string, string> = {};

    for (const dep of deps) {
      if (dep.name === currentField) continue;
      const v = ctx.watch(dep.name) as any;
      if (v == null || v === "") continue;

      const extractor = dep.extract ?? "text";
      const bucket = dep.bucket ?? "equals";

      let out: any;
      if (extractor === "id") out = v?.id;
      else if (extractor === "text")
        out = v?.text ?? (typeof v === "string" ? v : undefined);
      else out = v; // raw

      if (out === undefined) continue;

      if (bucket === "equals") {
        equals[dep.key] = out as any;
      } else {
        // contains must be strings
        const s = String(out);
        if (s.length) contains[dep.key] = s;
      }
    }

    return {
      equals: Object.keys(equals).length ? equals : undefined,
      contains: Object.keys(contains).length ? contains : undefined,
    };
  };
}
